<script setup lang="ts">
import { ref, shallowRef, watchEffect } from 'vue'
import type { ShallowRef } from 'vue'
import VIcon from '../VIcon/VIcon.vue'
import VDivider from '../VDivider/VDivider.vue'
import { EStates } from '../../composables/useCreateComponentConfig'
import useAccordionItemConfig from './composables/useAccordionItemConfig'
import type { IAccordionItemProps } from './types'
import { SIZES } from '@/ui/ts/types/Sizes'
import { isDark } from '@/ui/helpers/utils'

const props = defineProps<IAccordionItemProps>()
const emit = defineEmits(['update:isOpen'])

const contentElement = ref<HTMLElement | null>(null)

const accordionConfig: ShallowRef<Record<string, string>> = shallowRef({} as Record<string, string>)

function toggleAccordion(): void {
    if (props.state === EStates.DISABLED) {
        return
    }
    emit('update:isOpen', !props.isOpen)
}

watchEffect(() => {
    accordionConfig.value = useAccordionItemConfig(props)
})
</script>

<template>
    <div
        :class="[
            !(state === EStates.DISABLED) && 'hover:text-neutral-tertiary',
            accordionConfig.itemClasses,
            isDark(dark),
        ]"
        data-gpp-tools-ui
    >
        <div
            class="hover:text-neutral-tertiary flex w-full cursor-pointer justify-between"
            type="button"
            aria-expanded="true"
            aria-controls="accordion-content"
            :disabled="state === EStates.DISABLED"
            :class="[
                accordionConfig.triggerClasses,
                state === EStates.DISABLED && size === SIZES.SMALL && 'py-base text-disabled disabled:text-disabled',
                state === EStates.DISABLED && size === SIZES.MEDIUM && 'py-base text-disabled disabled:text-disabled',
                state === EStates.DISABLED && size === SIZES.LARGE && 'py-base text-disabled disabled:text-disabled',
            ]"
            @click="toggleAccordion"
        >
            <VIcon
                v-if="icon"
                :name="icon"
                :size="size"
            />
            <span
                v-if="item.title"
                class="mr-auto line-clamp-3 w-full text-left font-bold"
                :class="[
                    props.size === SIZES.LARGE && 'text-heading-xs',
                    props.size === SIZES.MEDIUM && 'text-label-lg',
                    props.size === SIZES.SMALL && 'text-label-md',
                ]"
            >
                {{ item.title }}
            </span>
            <div
                v-else
                class="w-full text-left"
            >
                <slot name="header" />
            </div>

            <VIcon
                class="AccordionChevron transition duration-500 ease-in-out"
                name="chevron-down"
                :class="{ 'rotate-180': isOpen, 'self-start': props.chevronTop }"
                :size="size"
            />
        </div>
        <div
            class="accordion__wrapper"
            :class="{ 'accordion__wrapper--open': isOpen }"
        >
            <div
                ref="contentElement"
                class="accordion__content-fade accordion__content transition-all duration-500 ease-in-out"
                :aria-hidden="!isOpen"
                :class="[state === EStates.DISABLED && 'text-disabled']"
            >
                <div
                    v-if="item.content"
                    class="text-paragraph-md font-medium"
                    :class="[
                        accordionConfig.contentClasses,
                        isOpen ? 'accordion__content-fade-in' : 'accordion__content-fade-out',
                    ]"
                >
                    {{ item.content }}
                </div>
                <div
                    v-else
                    :class="[
                        accordionConfig.contentClasses,
                        isOpen ? 'accordion__content-fade-in' : 'accordion__content-fade-out',
                    ]"
                >
                    <slot />
                </div>
            </div>
        </div>
    </div>
    <VDivider
        v-show="!hideDivider"
        :dark="dark"
    />
</template>

<style scoped>
.accordion__wrapper{display:grid;grid-template-rows:0fr;transition:grid-template-rows .5s ease-in-out}.accordion__content{overflow:hidden}.accordion__wrapper--open{grid-template-rows:1fr}.accordion__content-fade-in{animation:fadeIn .4s linear}.accordion__content-fade-out{animation:fadeOut .4s linear}@keyframes fadeIn{0%{opacity:0}50%{opacity:0}to{opacity:1}}@keyframes fadeOut{0%{opacity:1}50%{opacity:0}to{opacity:0}}
</style>
